import React, { useRef } from 'react';

import styledC from 'styled-components';

import {
  useIsContentOverflowing,
  parseFilename,
} from '@adsk/alloy-react-helpers';
import { useTheme } from '@adsk/alloy-react-styles';
import Tooltip from '@adsk/alloy-react-tooltip';

type FilenameTooltipProps = Omit<
  React.ComponentProps<typeof Tooltip>,
  'content' | 'children'
> & {
  name: string;
  /**
   * @deprecated This callback is no longer used and will be removed in the next major version.
   * The component now uses pure CSS-based truncation (text-overflow: ellipsis) which is
   * more performant and deterministic. The visible text cannot be reliably calculated
   * with CSS truncation, making this callback obsolete.
   */
  onVisibleNameChange?: (visibleName: string) => void;
};

const FilenameDisplay = styledC.div`
  display: flex;
  align-items: center;
  min-width: 0;
  text-align: left;
  width: 100%;
`;

const FileExtension = styledC.span`
  flex-shrink: 0;
`;

/**
 * A specialized Tooltip for showing filenames. It automatically clips the overflowing text
 * using CSS, while always showing the file extension (last part).
 *
 * The tooltip appears only when the filename is truncated.
 */
const FilenameTooltip = ({
  name,
  getTargetProps,
  ...tooltipProps
}: FilenameTooltipProps) => {
  const theme = useTheme();
  const truncationTokens =
    theme.tokens?.component?.fileHandler?.['filename-tooltip'];

  const filenameRef = useRef<HTMLDivElement>(null);
  const isOverflowing = useIsContentOverflowing(filenameRef);

  const { baseName, extension } = parseFilename(name);

  const targetProps = getTargetProps?.() ?? {};

  const content = (
    <FilenameDisplay dir="auto" data-testid="FilenameTooltip__VisibleText">
      <span ref={filenameRef} css={truncationTokens}>
        {baseName}
      </span>
      {extension && <FileExtension>{extension}</FileExtension>}
    </FilenameDisplay>
  );

  if (!isOverflowing) {
    return (
      <div
        {...targetProps}
        style={{
          ...(targetProps?.style ?? {}),
          width: '100%',
          position: 'relative',
          textDecoration: 'inherit',
        }}
      >
        {content}
      </div>
    );
  }

  return (
    <Tooltip
      placement={Tooltip.PLACEMENTS.TOP}
      {...tooltipProps}
      content={name}
      getTargetProps={() => ({
        ...targetProps,
        style: {
          ...(targetProps?.style ?? {}),
          width: '100%',
          position: 'relative',
          textDecoration: 'inherit',
        },
      })}
    >
      {content}
    </Tooltip>
  );
};

FilenameTooltip.displayName = 'FilenameTooltip';

/*
 * Excluding content because that is set on the Tooltip to be the original filename
 * and children in an effort to make it more evident that name has to be
 * a string for this component's computation to work reliably
 */
const { content: _, children: _2, ...baseTooltipProps } = Tooltip.propTypes;

FilenameTooltip.propTypes = {
  ...baseTooltipProps,
};

export default FilenameTooltip;
